import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle, Coins } from "lucide-react";

const MAX_POLLS = 20;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshCredits } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX_POLLS) { setState("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setInfo(data);
        await refreshCredits();
        setState("paid");
        return;
      }
      if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
    } catch (e) {
      setState("error");
      return;
    }
    setTimeout(() => poll(attempt + 1), 2000);
    // Recursive polling by design; MAX_POLLS/api are stable module-level refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, refreshCredits]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center py-12 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <Card className="flex w-full flex-col items-center gap-4 p-12" data-testid="payment-checking">
          <Loader2 size={32} className="animate-spin text-primary" />
          <h1 className="font-head text-2xl font-bold">Confirming your payment…</h1>
          <p className="text-sm text-muted-foreground">Just a moment while we credit your account.</p>
        </Card>
      )}
      {state === "paid" && (
        <Card className="flex w-full flex-col items-center gap-4 p-12" data-testid="payment-paid">
          <CheckCircle2 size={44} className="text-accent" />
          <h1 className="font-head text-3xl font-bold">You're all set!</h1>
          <p className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm font-semibold">
            <Coins size={15} className="text-primary" /> {info?.credits} credits added
          </p>
          <p className="text-sm text-muted-foreground">Your balance has been updated. Time to create.</p>
          <div className="mt-2 flex gap-3">
            <Button onClick={() => navigate("/dashboard")} data-testid="success-dashboard-btn"
              className="rounded-full bg-primary hover:bg-primary/90">Go to dashboard</Button>
            <Button variant="outline" onClick={() => navigate("/campaigns/new")}
              className="rounded-full" data-testid="success-new-campaign-btn">New campaign</Button>
          </div>
        </Card>
      )}
      {(state === "timeout" || state === "error") && (
        <Card className="flex w-full flex-col items-center gap-4 p-12" data-testid="payment-error">
          <XCircle size={40} className="text-muted-foreground" />
          <h1 className="font-head text-2xl font-bold">
            {state === "timeout" ? "Still processing…" : "Something went wrong"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {state === "timeout"
              ? "Your payment is taking a little longer than usual. Your credits will appear shortly."
              : "We couldn't confirm this payment. If you were charged, your credits will still be added."}
          </p>
          <div className="mt-2 flex gap-3">
            <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-primary hover:bg-primary/90">
              Back to dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate("/pricing")} className="rounded-full">Pricing</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
